import { useState, useEffect, useCallback } from "react";
import { notifyError, notifySuccess } from "src/assets/toastService";
import { Category, Product, StaffMember } from "src/shared/services/interfacev2";
import { getCategory, getStaff, putProduct } from "src/shared/services/servicesv2";
import { errorHandling } from "src/shared/utils/errorHandling";

interface UseEditProductProps {
  product: Product;
  onClose: () => void;
  onUpdate: () => void;
  open: boolean;
}

export const useEditProduct = ({ product, onClose, onUpdate, open }: UseEditProductProps) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [staffList, setStaffList] = useState<StaffMember[]>([]);
  const [formData, setFormData] = useState({
    productName: product.productName || "",
    arabicName: product.arabicName || "",
    amount: product.amount || "",
    categoryId: product.categoryId || "",
    staffList: [] as string[], // Array of staff IDs for the form
    type: product.type || "",
    image: null as File | null,
  });
  const [previewImage, setPreviewImage] = useState<string | null>(product.imageUrl || null);
  const [hasChanges, setHasChanges] = useState<boolean>(false);
  const [isFormValid, setIsFormValid] = useState<boolean>(false);

  const getCategoryInitials = useCallback((categoryName: string): string => {
    const words = categoryName.trim().split(/\s+/);
    if (words.length === 1) {
      return words[0].substring(0, 5).toUpperCase();
    } else {
      return words.map((word) => word[0].toUpperCase()).join("");
    }
  }, []);

  const updateProductNameWithCategory = useCallback(
    (productName: string, categoryId: string): string => {
      if (!categoryId || !categories.length) return productName;

      const selectedCategory = categories.find((cat) => cat._id.$oid === categoryId);
      if (!selectedCategory) return productName;

      const categoryInitials = getCategoryInitials(selectedCategory.name);

      const cleanedName = productName.replace(/\s*\[[^\]]+\]/g, "").trim();

      return `${cleanedName} [${categoryInitials}]`;
    },
    [categories, getCategoryInitials],
  );

  // Helper to get staff names for display
  const getSelectedStaffNames = useCallback(() => {
    return staffList.filter((staff) => formData.staffList.includes(staff._id.$oid)).map((staff) => staff.staff_name || staff.firstName);
  }, [formData.staffList, staffList]);

  useEffect(() => {
    if (!open) return;

    const fetchData = async () => {
      setIsLoading(true);

      try {
        const categoryRes = await getCategory();
        setCategories(categoryRes.data.categoryDetails);

        const staffRes = await getStaff();
        const staffData: StaffMember[] = staffRes.data?.result || [];
        setStaffList(staffData);

        // FIX: Convert product.staffList (which has firstName) to staff IDs
        const productStaffNames: string[] = product.staffList || [];

        const selectedIds = staffData.filter((staff) => productStaffNames.includes(staff.firstName)).map((staff) => staff._id.$oid);

        setFormData((prev) => ({ ...prev, staffList: selectedIds }));
      } catch (error) {
        errorHandling(error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [open, product]);

  useEffect(() => {
    if (staffList.length > 0 && product.staffList) {
      const productStaffNames: string[] = product.staffList;

      const selectedIds = staffList.filter((staff) => productStaffNames.includes(staff.firstName)).map((staff) => staff._id.$oid);

      setFormData((prev) => ({
        ...prev,
        staffList: selectedIds,
      }));
    }
  }, [staffList, product.staffList]);

  useEffect(() => {
    const productStaffNames: string[] = product.staffList || [];
    const currentStaffIds = staffList.filter((staff) => productStaffNames.includes(staff.firstName)).map((staff) => staff._id.$oid);

    const hasFormChanges =
      formData.productName.trim() !== product.productName.trim() ||
      formData.arabicName.trim() !== product.arabicName.trim() ||
      formData.amount.toString() !== product.amount.toString() ||
      formData.categoryId !== product.categoryId ||
      formData.type !== (product.type || "") ||
      formData.image !== null ||
      JSON.stringify(formData.staffList.sort()) !== JSON.stringify(currentStaffIds.sort());

    setHasChanges(hasFormChanges);

    // ✅ Amount validation like AddProductModal
    const amountValue = parseFloat(formData.amount);
    const isValid =
      formData.productName.trim() !== "" &&
      formData.arabicName.trim() !== "" &&
      formData.categoryId.trim() !== "" &&
      !isNaN(amountValue) &&
      amountValue > 0;

    setIsFormValid(isValid);
  }, [formData, product, staffList]);

  const handleInputChange = useCallback(
    (field: string, value: string | string[] | File | null) => {
      setFormData((prev) => {
        const newFormData = { ...prev, [field]: value };

        if (field === "categoryId" && typeof value === "string" && value) {
          const updatedProductName = updateProductNameWithCategory(prev.productName, value);
          newFormData.productName = updatedProductName;
        }

        return newFormData;
      });
    },
    [updateProductNameWithCategory],
  );

  const handleFileChange = useCallback(
    (file: File | null) => {
      if (file) {
        const reader = new FileReader();
        reader.onload = () => setPreviewImage(reader.result as string);
        reader.readAsDataURL(file);
        handleInputChange("image", file);
      }
    },
    [handleInputChange],
  );

  const handleUpdate = useCallback(async () => {
    if (!isFormValid) return;

    setIsLoading(true);

    // Get staff firstNames from selected IDs for API submission
    const selectedStaffNames = staffList.filter((staff) => formData.staffList.includes(staff._id.$oid)).map((staff) => staff.firstName); // Use firstName for API

    const updateFormData = new FormData();
    updateFormData.append("productName", formData.productName);
    updateFormData.append("amount", formData.amount);
    updateFormData.append("arabicName", formData.arabicName);
    updateFormData.append("categoryId", formData.categoryId);

    if (formData.type) {
      updateFormData.append("type", formData.type);
    }

    // Send as array of staff firstNames
    updateFormData.append("staffList", JSON.stringify(selectedStaffNames));

    if (formData.image) {
      updateFormData.append("image", formData.image);
    }
    try {
      await putProduct(product._id.$oid, updateFormData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      notifySuccess("Product updated successfully");
      onClose();
      onUpdate();
    } catch (error: any) {
      console.error("Error updating product:", error);

      // const errorMessage =
      //   error?.response?.data?.message ||
      //   error?.response?.data?.error ||
      //   error?.message ||
      //   "Failed to update product";

      // notifyError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [formData, isFormValid, staffList, product, onUpdate, onClose]);

  const removeEmojis = useCallback((str: string): string => {
    return str.replace(/([\u2700-\u27BF]|[\uE000-\uF8FF]|\u24C2|\uD83C[\uDC00-\uDFFF]|\uD83D[\uDC00-\uDFFF]|\uD83E[\uDD00-\uDFFF])/g, "");
  }, []);

  const formatAmount = useCallback((value: string): string => {
    if (!value) return "0.00";
    let [integerPart, decimalPart] = value.split(".");
    if (!integerPart) integerPart = "0";
    if (!decimalPart) decimalPart = "00";
    else if (decimalPart.length === 1) decimalPart = decimalPart + "0";
    else if (decimalPart.length > 2) decimalPart = decimalPart.slice(0, 2);
    return `${integerPart}.${decimalPart}`;
  }, []);

  return {
    isLoading,
    categories,
    staffList,
    formData,
    previewImage,
    hasChanges,
    isFormValid,
    handleInputChange,
    handleFileChange,
    handleUpdate,
    removeEmojis,
    formatAmount,
    getCategoryInitials,
    updateProductNameWithCategory,
    getSelectedStaffNames,
  };
};
